<script setup lang="ts">
import { computed, markRaw, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { PlusIcon } from '@heroicons/vue/24/outline'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import Modal from '@/components/common/layout/Modal.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import CompanyCell from '@/components/data-visualization/grids/CompanyCell.vue'
import ColumnSelector from '@/components/data-visualization/grids/ColumnSelector.vue'
import CounterCard from '@/components/common/ui/CounterCard.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import { useToast } from 'vue-toast-notification'
import { useLeadStore } from '@/stores/useLeadStore'
import { useOrderStore } from '@/stores/useOrderStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useMyTeamStore } from '@/stores/useMyTeamStore'
import type { ColumnConfig } from '@/components/data-visualization/grids/ColumnSelector.vue'
import LeadGridActions from '@/components/features/leads/LeadGridActions.vue'
import { dateComparator, formatDate } from '@/utils/dateUtils'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import LeadBadgeGrid from '@/components/data-visualization/grids/LeadBadgeGrid.vue'


const $toast = useToast()
const router = useRouter()
const leadStore = useLeadStore()
const orderStore = useOrderStore()
const boostCoreStore = useBoostCorePermissionStore()
const myTeamStore = useMyTeamStore()
const activeTab = ref<'recu' | 'envoye'>('recu')
const showCreateLeadModal = ref(false)
const isLoadingCreateLead = ref(false)

const newLead = ref({
  type: 'COOPTATION',
  source: 'CRM',
  recipient: 'ITAC',
  contactFirstname: '',
  contactLastname: '',
  contactEmail: '',
  contactPhone: '',
  companyName: '',
  companySiret: '',
  familyCodes: [] as string[],
  data: {
    notes: '',
  },
})

const familyOptions = computed(() =>
  orderStore.ventilationFamilies.map((f) => ({
    id: f.code,
    value: f.code,
    label: f.label,
  }))
)

const leadRecipients = [
  { label: 'ITAC', value: 'ITAC' },
  { label: 'UNIXO', value: 'UNIXO' },
  { label: 'GROUP SOLUTIONS', value: 'GROUP_SOLUTIONS' },
]

const leadStatusOptions = [
  { label: 'En attente', value: 'WAITING', order: 1, color: 'warning' },
  { label: 'Accepté', value: 'ACCEPTED', order: 2, color: 'info' },
  { label: 'Gagné', value: 'WON', order: 4, color: 'success' },
  { label: 'Perdu', value: 'LOST', order: 5, color: 'error' },
  { label: 'Refusé', value: 'REFUSED', order: 6, color: 'error' },
]

const filters = ref<Record<string, any>>({})
const searchQuery = ref<string>('')
const recipientFilter = ref<string>('')
const authorFilter = ref<string>('')
const ownerFilter = ref<string>('')
const selectedStatusFilter = ref<string | null>(null)

const isSuperAdmin = computed(() => boostCoreStore.include('SUPER_ADMIN'))

const availableAuthors = computed(() => {
  if (isSuperAdmin.value) {
    // Pour SUPER_ADMIN, on pourrait récupérer tous les utilisateurs
    // Pour l'instant, on utilise l'équipe actuelle
    return myTeamStore.all.map(member => ({
      id: member.uuid,
      value: member.uuid,
      label: `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email
    }))
  } else {
    // Pour les managers normaux, seulement leurs subordonnés
    return myTeamStore.members.map(member => ({
      id: member.uuid,
      value: member.uuid,
      label: `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email
    }))
  }
})

const allColumns = computed<ColumnConfig[]>(() => [
  {
    field: 'type',
    headerName: 'Type',
    visible: true,
  },
  {
    field: 'recipient',
    headerName: 'Destinataire',
    visible: true,
  },
  {
    field: 'contactFirstname',
    headerName: 'Prénom',
    visible: true,
  },
  {
    field: 'contactLastname',
    headerName: 'Nom',
    visible: true,
  },
  {
    field: 'contactEmail',
    headerName: 'Email',
    visible: true,
  },
  {
    field: 'contactPhone',
    headerName: 'Téléphone',
    visible: true,
  },
  {
    field: 'companyName',
    headerName: 'Société / SIRET',
    visible: true,
  },
  {
    field: 'status',
    headerName: 'Statut',
    visible: true,
  },
  {
    field: 'ownerUuid',
    headerName: 'Attribué à',
    visible: true,
  },
  {
    field: 'authorUuid',
    headerName: 'Auteur',
    visible: true,
  },
  {
    field: 'createdAt',
    headerName: 'Créé le',
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    visible: true,
  },
])

const visibleColumnFields = ref<string[]>([
  'type',
  'recipient',
  'contactFirstname',
  'contactLastname',
  'contactEmail',
  'contactPhone',
  'companyName',
  'status',
  'ownerUuid',
  'authorUuid',
  'createdAt',
  'actions',
])

function onColumnsChanged(newVisibleColumns: string[]) {
  visibleColumnFields.value = newVisibleColumns
}

const gridColumns = computed(() => {
  const visibleFields = new Set(visibleColumnFields.value)
  const baseColumns = [
    { field: 'type', headerName: 'Type' },
    { field: 'recipient', headerName: 'Destinataire' },
    { field: 'contactFirstname', headerName: 'Prénom' },
    { field: 'contactLastname', headerName: 'Nom' },
    { field: 'contactEmail', headerName: 'Email' },
    { field: 'contactPhone', headerName: 'Téléphone' },
    {
      field: 'companyName',
      headerName: 'Société / SIRET',
      cellRenderer: CompanyCell,
    },
    {
      field: 'ownerUuid',
      headerName: 'Attribué à',
      valueGetter: (params: any) => {
        const owner = params.data?.owner
        if (owner?.firstName || owner?.lastName) {
          return `${owner.firstName || ''} ${owner.lastName || ''}`.trim()
        }
        return 'Non attribué'
      },
    },
    {
      field: 'authorUuid',
      headerName: 'Auteur',
      valueGetter: (params: any) => {
        const author = params.data?.author
        const authorMetadata = params.data?.metadata?.authorName
        if(author?.firstName || author?.lastName) return `${author.firstName || ''} ${author.lastName || ''}`.trim()
        if(authorMetadata) return authorMetadata
        return 'Non défini'
      },
    },
    {
      field: 'createdAt',
      headerName: 'Créé le',
      valueFormatter: (params: any) => formatDate(params.value?.toString() || ''),
      filter: 'agDateColumnFilter',
      comparator: dateComparator,
    },
    {
      field: 'status',
      headerName: 'Statut',
      cellRenderer: LeadBadgeGrid,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      resizable: false,
      filter: false,
      sortable: false,
      minWidth: 120,
      maxWidth: 120,
      cellRenderer: markRaw(LeadGridActions),
      cellRendererParams: {
        tooltipPosition: 'left',
        onClickView: (params: any) => {
          openLeadDetails(params)
        },
      },
    },
  ]
  return baseColumns.filter((col: any) => visibleFields.has(col.field))
})

const pageSize = ref<number>(10)

const isCreateLeadFormValid = computed(() => {
  const payload = newLead.value
  return (
    payload.type.trim().length > 0 &&
    payload.source.trim().length > 0 &&
    payload.recipient.trim().length > 0 &&
    payload.contactFirstname.trim().length > 0 &&
    payload.contactLastname.trim().length > 0 &&
    payload.companyName.trim().length > 0 &&
    payload.companySiret.trim().length > 0 &&
    (payload.contactEmail.trim().length > 0 || payload.contactPhone.trim().length > 0)
  )
})

const leadStatusCounts = computed(() => {
  const leads = leadStore.statusCounts
  if(!leads) return {}


  const map: Record<string, { count: number; sumMarge: number }> = {
    WAITING: { count: 0, sumMarge: 0 },
    ACCEPTED: { count: 0, sumMarge: 0 },
    REFUSED: { count: 0, sumMarge: 0 },
    WON: { count: 0, sumMarge: 0 },
    LOST: { count: 0, sumMarge: 0 },
  }

  leadStatusOptions.forEach(status => {
    const count = leads[status.value] || 0
    map[status.value] = { count, sumMarge: 0 }
  })
  return map
})

const counterStatusOptions = computed(() => {
  return [...leadStatusOptions].sort((a, b) => a.order - b.order)
})

function onPaginationChanged(newPageSize: number, newPage: number) {
  pageSize.value = newPageSize
  if (activeTab.value === 'recu') {
    leadStore.fetchReceivedLeads(newPageSize, newPage, filters.value)
  } else {
    leadStore.fetchSentLeads(newPageSize, newPage, filters.value)
  }
}

function onCounterCardClick(statusValue: string) {
  if (selectedStatusFilter.value === statusValue) {
    selectedStatusFilter.value = null
  } else {
    selectedStatusFilter.value = statusValue
  }
  applyFilters()
}

async function openLeadDetails(lead: any) {
  if (!lead?.uuid) return
  router.push(`/dashboard/leads/${lead.uuid}`)
}

async function fetchActiveLeads() {
  if (activeTab.value === 'recu') {
    await leadStore.fetchReceivedLeads(pageSize.value, 1, filters.value)
  } else {
    await leadStore.fetchSentLeads(pageSize.value, 1, filters.value)
  }
}

function applyFilters() {
  const newFilters: Record<string, any> = {}

  if (selectedStatusFilter.value) {
    newFilters.status = selectedStatusFilter.value
  }

  if (recipientFilter.value.trim()) {
    newFilters.recipient = recipientFilter.value
  }

  if (searchQuery.value.trim()) {
    newFilters.search = searchQuery.value
  }

  if (authorFilter.value.trim()) {
    newFilters.authorUuid = [authorFilter.value]
  }

  if (ownerFilter.value.trim()) {
    newFilters.ownerUuid = [ownerFilter.value]
  }

  filters.value = newFilters
  fetchActiveLeads()
}

// Watcher pour la recherche en temps réel avec debounce
let searchTimeout: number | null = null
watch(searchQuery, () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    applyFilters()
  }, 300) // Debounce de 300ms
})

watch(activeTab, async () => {
  filters.value = {}
  searchQuery.value = ''
  recipientFilter.value = ''
  authorFilter.value = ''
  ownerFilter.value = ''
  selectedStatusFilter.value = null
  await fetchActiveLeads()
})

const openCreateLeadModal = () => {
  showCreateLeadModal.value = true
}

const closeCreateLeadModal = () => {
  showCreateLeadModal.value = false
}

const resetCreateLeadForm = () => {
  newLead.value = {
    type: 'COOPTATION',
    source: 'CRM',
    recipient: 'UNIXO',
    contactFirstname: '',
    contactLastname: '',
    contactEmail: '',
    contactPhone: '',
    companyName: '',
    companySiret: '',
    familyCodes: [],
    data: {
      notes: '',
    },
  }
}

const formatLeadPhone = (phone: string) => {
  const cleaned = phone.replace(/\s/g, '').replace(/^0/, '')
  return cleaned ? `+33${cleaned}` : ''
}

const createLead = async () => {
  if (!isCreateLeadFormValid.value) return

  isLoadingCreateLead.value = true
  try {
    await leadStore.createLead({
      type: newLead.value.type,
      source: newLead.value.source,
      recipient: newLead.value.recipient,
      contactFirstname: newLead.value.contactFirstname,
      contactLastname: newLead.value.contactLastname,
      contactEmail: newLead.value.contactEmail || undefined,
      contactPhone: newLead.value.contactPhone ? formatLeadPhone(newLead.value.contactPhone) : undefined,
      companyName: newLead.value.companyName,
      companySiret: newLead.value.companySiret,
      familyCodes: newLead.value.familyCodes.length ? newLead.value.familyCodes : undefined,
      data: {
        notes: newLead.value.data.notes || undefined,
      },
    })
    $toast.success('Lead créé avec succès.')
    closeCreateLeadModal()
    resetCreateLeadForm()
    await fetchActiveLeads()
  } catch (error: unknown) {
    console.error('Erreur lors de la création du lead :', error)
    $toast.error('Échec de la création du lead. Veuillez réessayer.')
  } finally {
    isLoadingCreateLead.value = false
  }
}


onMounted(async () => {
  try {
    await fetchActiveLeads()
    await orderStore.ensureVentilationFamilies()
  } catch (error) {
    console.error('Erreur lors du chargement des leads:', error)
  }
})
</script>

<template>
  <div class="p-6">
    <div class="flex items-center justify-between gap-4">
      <Bs2Title title="Gestion des leads" subtitle="Consultez et gérez la liste de vos leads" />
    </div>

    <div class="mt-8 card bg-base-100 shadow">
      <div class="card-body">
        <div role="tablist" class="tabs tabs-boxed gap-2 bg-base-200 p-1.5 rounded-xl mb-2 shadow-inner justify-center">
          <button
            role="tab"
            class="tab gap-2 transition-all duration-300 h-10! rounded-xl hover:bg-base-100/50"
            :class="{
              'tab-active bg-primary! text-primary-content! shadow-md scale-[1.02]': activeTab === 'recu',
              'hover:text-primary': activeTab !== 'recu'
            }"
            @click="activeTab = 'recu'"
          >
            <span class="font-medium text-xs md:text-sm">Reçu</span>
          </button>
          <button
            role="tab"
            class="tab gap-2 transition-all duration-300 h-10 rounded-xl hover:bg-base-100/50"
            :class="{
              'tab-active bg-primary text-primary-content shadow-md scale-[1.02]': activeTab === 'envoye',
              'hover:text-primary': activeTab !== 'envoye'
            }"
            @click="activeTab = 'envoye'"
          >
            <span class="font-medium text-xs md:text-sm">Envoyé</span>
          </button>
        </div>

        <div v-if="activeTab === 'recu'" class="space-y-4">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p class="text-sm text-base-content/70">Liste des leads reçus</p>
            </div>
          </div>

          <div class="flex flex-nowrap gap-4 overflow-x-auto py-4">
            <CounterCard
              v-for="status in counterStatusOptions"
              :key="status.value"
              :counter="status"
              :status-counts="leadStatusCounts"
              :status-type="'lead'"
              :custom-color="status.color"
              :class="['flex-1 min-w-25 shrink-0 self-stretch my-1', { 'ring-2 ring-primary ring-offset-2': selectedStatusFilter === status.value }]"
              @click="onCounterCardClick(status.value)"
            />
          </div>

          <div class="flex flex-row flex-wrap items-end gap-2 justify-end">
            <div class="flex-1 min-w-50 max-w-full sm:max-w-75">
              <TextInput
                v-model="searchQuery"
                label="Recherche"
                placeholder="Prénom, nom ou société"
              />
            </div>
            <div class="flex-1 min-w-50 max-w-full sm:max-w-75">
              <label class="label mb-2">
                <span class="label-text">Destinataire</span>
              </label>
              <select
                v-model="recipientFilter"
                @change="applyFilters"
                class="select select-bordered w-full"
              >
                <option value="">Tous</option>
                <option v-for="recipient in leadRecipients" :key="recipient.value" :value="recipient.value">
                  {{ recipient.label }}
                </option>
              </select>
            </div>
            <div v-if="isSuperAdmin || myTeamStore.members.length > 0" class="flex-1 min-w-50 max-w-full sm:max-w-75">
              <label class="label mb-2">
                <span class="label-text">Voir en tant que</span>
              </label>
              <SelectInput
                v-model="ownerFilter"
                label=""
                :options="[ { id: '', label: 'Tous', value: '' }, ...availableAuthors ]"
                placeholder="Sélectionner un commercial"
                class="flex-1 min-w-[200px] max-w-full sm:max-w-[250px]"
                @close="applyFilters"
              />
            </div>
          </div>

          <div class="mt-4 card bg-base-100 shadow">
            <div class="card-body p-4 pt-2">
              <GridVue
                :list="leadStore.receivedLeads || []"
                :cols="gridColumns"
                :get-row-height="() => 48"
                :use-server-side-pagination="true"
                :pagination-page-size="pageSize"
                :pagination-total-rows="leadStore.totalReceivedLeads"
                :current-page="leadStore.currentReceivedPage"
                :total-pages="leadStore.totalReceivedPages"
                :show-export="false"
                @onPaginationChanged="onPaginationChanged"
              >
                <template #title>
                  <h2 class="card-title">Leads reçus</h2>
                </template>
                <template #filter>
                  <ColumnSelector :columns="allColumns" cookie-name="leads-columns-visibility" @columns-changed="onColumnsChanged" />
                </template>
              </GridVue>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'envoye'" class="space-y-4">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p class="text-sm text-base-content/70">Liste des leads envoyés</p>
            </div>
            <ButtonDaisy text="Créer un lead" variant="primary" :icon="PlusIcon" @click="openCreateLeadModal" />
          </div>

          <div class="flex flex-nowrap gap-4 overflow-x-auto py-4">
            <CounterCard
              v-for="status in counterStatusOptions"
              :key="status.value"
              :counter="status"
              :status-counts="leadStatusCounts"
              :status-type="'lead'"
              :custom-color="status.color"
              :class="['flex-1 min-w-25 shrink-0 self-stretch my-1', { 'ring-2 ring-primary ring-offset-2': selectedStatusFilter === status.value }]"
              @click="onCounterCardClick(status.value)"
            />
          </div>

          <div class="flex flex-row flex-wrap items-end gap-2 justify-end">
            <div class="flex-1 min-w-50 max-w-full sm:max-w-75">
              <TextInput
                v-model="searchQuery"
                label="Recherche"
                placeholder="Prénom, nom ou société"
              />
            </div>
            <div class="flex-1 min-w-50 max-w-full sm:max-w-75">
              <label class="label mb-2">
                <span class="label-text">Destinataire</span>
              </label>
              <select
                v-model="recipientFilter"
                @change="applyFilters"
                class="select select-bordered w-full"
              >
                <option value="">Tous</option>
                <option v-for="recipient in leadRecipients" :key="recipient.value" :value="recipient.value">
                  {{ recipient.label }}
                </option>
              </select>
            </div>
            <div v-if="isSuperAdmin || myTeamStore.members.length > 0" class="flex-1 min-w-50 max-w-full sm:max-w-75">
              <label class="label mb-2">
                <span class="label-text">Auteur</span>
              </label>
              <SelectInput
                v-model="authorFilter"
                label=""
                :options="[ { id: '', label: 'Tous', value: '' }, ...availableAuthors ]"
                placeholder="Sélectionner un commercial"
                class="flex-1 min-w-[200px] max-w-full sm:max-w-[250px]"
                @close="applyFilters"
              />
            </div>
          </div>

          <div class="mt-4 card bg-base-100 shadow">
            <div class="card-body p-4 pt-2">
              <GridVue
                :list="leadStore.sentLeads || []"
                :cols="gridColumns"
                :get-row-height="() => 48"
                :use-server-side-pagination="true"
                :pagination-page-size="pageSize"
                :pagination-total-rows="leadStore.totalSentLeads"
                :current-page="leadStore.currentSentPage"
                :total-pages="leadStore.totalSentPages"
                :show-export="false"
                @onPaginationChanged="onPaginationChanged"
              >
                <template #title>
                  <h2 class="card-title">Leads envoyés</h2>
                </template>
                <template #filter>
                  <ColumnSelector :columns="allColumns" cookie-name="leads-columns-visibility" @columns-changed="onColumnsChanged" />
                </template>
              </GridVue>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal :showModal="showCreateLeadModal" title="Créer un lead" @close="closeCreateLeadModal" maxWidth="3xl">
      <template #content>
        <div class="space-y-4 pt-4">
          <div class="grid gap-4 md:grid-cols-3">
            <div>
              <label class="label">
                <span class="label-text">Destinataire</span>
              </label>
              <select
                v-model="newLead.recipient"
                class="select select-bordered w-full"
              >
                <option v-for="recipient in leadRecipients" :key="recipient.value" :value="recipient.value">{{ recipient.label }}</option>
              </select>
            </div>
          </div>

          <div>
            <SelectInput
              v-model="newLead.familyCodes"
              label="Familles"
              :options="familyOptions"
              :multiple="true"
              placeholder="Sélectionner une ou plusieurs familles"
            />
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <label class="label">
                <span class="label-text">Prénom <span class="text-error">*</span></span>
              </label>
              <input
                type="text"
                v-model="newLead.contactFirstname"
                placeholder="Prénom"
                class="input input-bordered w-full"
              />
            </div>
            <div>
              <label class="label">
                <span class="label-text">Nom <span class="text-error">*</span></span>
              </label>
              <input
                type="text"
                v-model="newLead.contactLastname"
                placeholder="Nom"
                class="input input-bordered w-full"
              />
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <label class="label">
                <span class="label-text">Email (au moins un email ou un téléphone requis)</span>
              </label>
              <input
                type="email"
                v-model="newLead.contactEmail"
                placeholder="adresse@email.com"
                class="input input-bordered w-full"
              />
            </div>
            <div>
              <label class="label">
                <span class="label-text">Téléphone</span>
              </label>
              <div class="join w-full">
                <span class="btn join-item no-animation bg-base-200 pointer-events-none">+33</span>
                <input
                  type="tel"
                  v-model="newLead.contactPhone"
                  placeholder="6 12 34 56 78"
                  class="input input-bordered join-item w-full"
                />
              </div>
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <label class="label">
                <span class="label-text">Société <span class="text-error">*</span></span>
              </label>
              <input
                type="text"
                v-model="newLead.companyName"
                placeholder="Entreprise"
                class="input input-bordered w-full"
              />
            </div>
            <div>
              <label class="label">
                <span class="label-text">SIRET <span class="text-error">*</span></span>
              </label>
              <input
                type="text"
                v-model="newLead.companySiret"
                placeholder="SIRET"
                class="input input-bordered w-full"
              />
            </div>
          </div>

          <div class="divider">Informations supplémentaires</div>
          <div>
            <label class="label">
              <span class="label-text">Notes</span>
            </label>
            <textarea
              v-model="newLead.data.notes"
              placeholder="Informations complémentaires sur le lead..."
              class="textarea textarea-bordered w-full"
              rows="5"
            ></textarea>
          </div>

          <div class="modal-action justify-end">
            <button type="button" class="btn btn-ghost" @click="closeCreateLeadModal">Fermer</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!isCreateLeadFormValid || isLoadingCreateLead"
              @click="createLead"
            >
              {{ isLoadingCreateLead ? 'Création...' : 'Créer' }}
            </button>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>
